/**
 * Компонент основного контейнера виджета
 */

import { useEffect, useRef } from 'react';
import { Upload } from 'lucide-react';
import styles from './ChatContainer.module.css';
import { WidgetPosition } from '@/types/config.types';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { useDragDrop } from '@/hooks/useDragDrop';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useConfigStore } from '@/store/configStore';

/**
 * Пропсы компонента ChatContainer
 */
interface ChatContainerProps {
  /** Дочерние элементы (header, messages, input) */
  children: React.ReactNode;
  /** Виджет открыт */
  isOpen: boolean;
  /** Позиция виджета */
  position?: WidgetPosition;
  /** Стандартная кнопка открытия скрыта (например CSS на стороне клиента) — компактные отступы панели */
  launcherHidden?: boolean;
  /** Колбек при закрытии (для escape key) */
  onClose?: () => void;
  /** Колбек при drop файла */
  onFileDrop?: (file: File) => void;
}

/**
 * Основной контейнер виджета чата
 * 
 * @description Контейнер с фиксированной позицией, содержит все элементы виджета.
 * Fullscreen на мобильных устройствах.
 */
export function ChatContainer({
  children,
  isOpen,
  position = 'bottom-right',
  launcherHidden = false,
  onClose,
  onFileDrop,
}: ChatContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Swipe down = закрыть (только на мобильных)
  useSwipeGesture(containerRef, {
    minDistance: 100,
    onSwipeDown: () => {
      if (window.innerWidth <= 768) {
        onClose?.();
      }
    },
  });

  // Drag & Drop для загрузки файлов
  const { isDragging } = useDragDrop(containerRef, {
    onFileDrop: (file) => {
      onFileDrop?.(file);
    },
    acceptedTypes: ['image/*'],
  });


  const translations = useConfigStore((s) => s.translations);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  useScrollLock(isOpen && isMobile);

  // Если закрыт — не рендерим (убираем любые мигания)
  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className={`
        ${styles.container}
        ${position === 'bottom-left' ? styles.positionLeft : ''}
      `}
      data-panel-inset={launcherHidden ? 'tight' : 'default'}
      role="dialog"
      aria-modal="true"
      aria-label={translations.aria_chat || 'Chat with AI assistant'}
    >
      {children}

      {isDragging && (
        <div className={styles.dragOverlay}>
          <div className={styles.dragContent}>
            <Upload size={64} strokeWidth={2} />
            <div className={styles.dragText}>{translations.drag_title || 'Upload car photo'}</div>
            <div className={styles.dragHint}>
              {translations.drag_hint || 'Drop to identify the model and find matching LARTE kits'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

